import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Sparkles, Trash2, Loader2, X, Star, ChevronRight, Image as ImageIcon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CameraButton({ onCapture, disabled, label, testId }) {
  const fileRef = useRef(null);
  return (
    <>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={disabled}
        className="neo-btn px-3 py-2 bg-white font-bold text-sm flex items-center gap-1.5 disabled:opacity-50"
        data-testid={testId}
      >
        <Camera className="w-4 h-4" /> {label}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onloadend = () => onCapture(reader.result);
          reader.readAsDataURL(file);
          e.target.value = '';
        }}
      />
    </>
  );
}

function MatchCard({ match, isBest }) {
  const [open, setOpen] = useState(isBest);
  return (
    <div
      className={`neo-card p-3 ${isBest ? 'bg-gradient-to-br from-[#FFF7B5] to-[#FFC8DD]' : 'bg-white'}`}
      data-testid={`lookalike-match-${match.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2">
        <span className="text-3xl shrink-0">{match.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <h3 className="font-bold text-base leading-tight truncate">{match.name}</h3>
            {isBest && <span className="text-[10px] font-bold bg-amber-400 px-1.5 py-0.5 rounded-full">⭐ Bäst match</span>}
          </div>
          <p className="text-[11px] opacity-70">{match.era}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold font-['Fredoka'] leading-none">{match.similarity}%</div>
          <div className="text-[9px] opacity-60 uppercase tracking-wide">likhet</div>
        </div>
        <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-2.5 border-t-2 border-black/10 pt-3">
          {/* Shared features */}
          <div>
            <h4 className="font-bold text-xs uppercase opacity-70 mb-1">Vad ni har gemensamt</h4>
            <div className="flex flex-wrap gap-1">
              {match.shared_features.map((f, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full border-2 border-black bg-white text-[11px] font-bold">
                  ✨ {f}
                </span>
              ))}
            </div>
          </div>

          {/* Signature look */}
          {match.signature_look && (
            <div className="p-2 rounded-lg bg-black/5">
              <h4 className="font-bold text-xs flex items-center gap-1 mb-1">
                <Star className="w-3 h-3" /> Signatur-look
              </h4>
              <p className="text-xs leading-snug">{match.signature_look}</p>
            </div>
          )}

          {/* Tips grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <TipBox emoji="💇" label="Hår" text={match.styling_tips.hair} />
            <TipBox emoji="💄" label="Smink" text={match.styling_tips.makeup} />
            <TipBox emoji="👗" label="Kläder" text={match.styling_tips.clothes} />
            <TipBox emoji="📸" label="Pose" text={match.styling_tips.pose} />
          </div>
        </div>
      )}
    </div>
  );
}

function TipBox({ emoji, label, text }) {
  if (!text) return null;
  return (
    <div className="p-2 rounded-lg bg-white border-2 border-black/10">
      <div className="text-[10px] font-bold uppercase opacity-70 flex items-center gap-1 mb-0.5">
        <span>{emoji}</span> {label}
      </div>
      <p className="text-xs leading-snug">{text}</p>
    </div>
  );
}

function EntryDetail({ entryId, onBack, onDeleted }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lookalike/entries/${entryId}`, { withCredentials: true });
        if (!cancel) setEntry(data);
      } catch {
        toast.error('Kunde inte ladda');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [entryId]);

  const remove = async () => {
    if (!window.confirm('Ta bort denna analys?')) return;
    try {
      await axios.delete(`${API}/lookalike/entries/${entryId}`, { withCredentials: true });
      toast.success('Borttagen');
      onDeleted();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  if (loading || !entry) {
    return <div className="neo-card p-8 bg-white text-center">Laddar...</div>;
  }

  return (
    <div className="space-y-3" data-testid="lookalike-entry-detail">
      <header className="neo-card p-3 bg-gradient-to-br from-[#FFF7B5] to-[#FFE8F5] flex items-center gap-2">
        <button onClick={onBack} className="neo-btn p-1.5 bg-white" aria-label="Tillbaka" data-testid="lookalike-back">
          <X className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm leading-tight">Din analys</h3>
          <p className="text-[11px] opacity-70 truncate">{entry.category_label}</p>
        </div>
        <button onClick={remove} className="neo-btn p-1.5 bg-white text-red-600" data-testid="lookalike-delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </header>

      {entry.image_base64 && (
        <img
          src={entry.image_base64.startsWith('data:') ? entry.image_base64 : `data:image/jpeg;base64,${entry.image_base64}`}
          alt="Du"
          className="w-32 h-32 rounded-full mx-auto border-4 border-black object-cover shadow-lg"
        />
      )}

      {entry.observation && (
        <div className="neo-card p-3 bg-[#FFF7B5] text-sm italic leading-relaxed">
          {entry.observation}
        </div>
      )}

      {entry.matches.map((m, i) => (
        <MatchCard key={i} match={m} isBest={i === 0} />
      ))}

      {entry.best_match_reason && (
        <div className="neo-card p-3 bg-white text-xs italic opacity-80">
          <strong>Varför {entry.matches[0]?.name} är bästa matchen:</strong> {entry.best_match_reason}
        </div>
      )}
    </div>
  );
}

export default function CelebMirror() {
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedCategory, setPickedCategory] = useState('any');
  const [pickedImage, setPickedImage] = useState(null);
  const [notes, setNotes] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, ents] = await Promise.all([
        axios.get(`${API}/lookalike/categories`, { withCredentials: true }),
        axios.get(`${API}/lookalike/entries`, { withCredentials: true }),
      ]);
      setCategories(cats.data.categories || []);
      setEntries(ents.data.entries || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const analyze = async () => {
    if (!pickedImage) {
      toast.error('Ladda upp en selfie först');
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(
        `${API}/lookalike/analyze`,
        { image_base64: pickedImage, category: pickedCategory, notes: notes || null },
        { withCredentials: true, timeout: 90000 }
      );
      toast.success('🌟 Analysen är klar!');
      setActiveId(data.id);
      setPickedImage(null);
      setNotes('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte analysera');
    } finally {
      setAnalyzing(false);
    }
  };

  if (activeId) {
    return (
      <EntryDetail
        entryId={activeId}
        onBack={() => setActiveId(null)}
        onDeleted={() => { setActiveId(null); fetchAll(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="celeb-mirror">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFF7B5] via-[#FFC8DD] to-[#C8B6FF]">
        <h2 className="font-['Fredoka'] font-bold text-xl flex items-center gap-2">
          <Star className="w-5 h-5" /> Kändis-spegel
        </h2>
        <p className="text-sm opacity-80 mt-1">
          Vilken kändis är du lik? Få 4 träffar med tips på hår, smink, kläder och pose för att accentuera likheten.
        </p>
      </header>

      {/* New analysis card */}
      <div className="neo-card p-4 bg-white space-y-3" data-testid="lookalike-form">
        <h3 className="font-bold text-sm">Ny analys</h3>

        {/* Image picker */}
        {!pickedImage ? (
          <div className="border-2 border-dashed border-black/30 rounded-xl p-6 text-center bg-[#FFF8F0]">
            <ImageIcon className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <p className="text-xs opacity-70 mb-3">Ladda upp en selfie där ditt ansikte syns tydligt</p>
            <div className="flex justify-center gap-2 flex-wrap">
              <CameraButton onCapture={setPickedImage} testId="lookalike-camera" label="Ta selfie" disabled={analyzing} />
              <CameraButton onCapture={setPickedImage} testId="lookalike-album" label="Från album" disabled={analyzing} />
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <img src={pickedImage} alt="Du" className="w-24 h-24 rounded-xl border-2 border-black object-cover" />
            <button
              onClick={() => setPickedImage(null)}
              className="neo-btn p-1.5 bg-white text-red-600"
              data-testid="lookalike-clear-image"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Category */}
        <div>
          <label className="block text-xs font-bold mb-1.5">Vilken typ av kändis?</label>
          <div className="flex flex-wrap gap-1.5" data-testid="lookalike-categories">
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => setPickedCategory(c.key)}
                className={`neo-btn px-2.5 py-1.5 text-xs flex items-center gap-1 ${
                  pickedCategory === c.key ? 'bg-[#FFC8DD] font-bold' : 'bg-white'
                }`}
                data-testid={`lookalike-cat-${c.key}`}
              >
                <span>{c.emoji}</span> {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-bold mb-1.5">Önskemål (valfritt)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex: 'Helst nordiska skådespelerskor' eller 'med långt hår'"
            maxLength={200}
            className="neo-input w-full text-sm"
            data-testid="lookalike-notes"
          />
        </div>

        <button
          onClick={analyze}
          disabled={!pickedImage || analyzing}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="lookalike-analyze"
        >
          {analyzing ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Analyserar... (15-25s)</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Hitta mina kändis-matchningar</>
          )}
        </button>
      </div>

      {/* Past entries */}
      <div>
        <h3 className="font-bold text-sm mb-2">Tidigare analyser</h3>
        {loading ? (
          <p className="text-xs opacity-60">Laddar...</p>
        ) : entries.length === 0 ? (
          <div className="neo-card p-4 bg-white text-center text-sm opacity-70">
            Inga analyser än — ladda upp en selfie ovan för att börja 💛
          </div>
        ) : (
          <div className="grid gap-2 grid-cols-1 md:grid-cols-2" data-testid="lookalike-entries-list">
            {entries.map((e) => (
              <button
                key={e.id}
                onClick={() => setActiveId(e.id)}
                className="neo-card p-3 bg-white text-left hover:bg-[#FFE8F5] transition flex items-center gap-2"
                data-testid={`lookalike-entry-${e.id}`}
              >
                <div className="flex -space-x-2">
                  {e.matches.slice(0, 3).map((m, i) => (
                    <div key={i} className="w-9 h-9 rounded-full border-2 border-black bg-[#FFF7B5] flex items-center justify-center text-lg">
                      {m.emoji}
                    </div>
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{e.matches[0]?.name || 'Analys'}</p>
                  <p className="text-[10px] opacity-60 truncate">
                    {e.matches[0]?.similarity}% likhet · {new Date(e.created_at).toLocaleDateString('sv-SE')}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 shrink-0 opacity-50" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
